/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { MutateOptions } from '@tanstack/react-query';
import { isEqual, noop } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isDefined } from '~shared/helpers/types';
import { getJiraWorkTypeMandatoryFields, getSelectedWorkTypeIds } from '../helpers';
import { useJiraWorkTypesQuery, usePatchJiraWorkTypesMutation } from '../queries';
import { JiraSelectedWorkTypes } from '../types';

export interface UseJiraWorkTypesOptions {
  jiraProjectKey?: string;
  preselect?: boolean;
  sonarOrganizationUuid: string;
  sonarProjectId: string;
}

type ApiOptions = {
  mutationOptions?: MutateOptions<void, Error, JiraSelectedWorkTypes>;
};

export function useJiraWorkTypes(options: UseJiraWorkTypesOptions & ApiOptions) {
  const { jiraProjectKey, sonarOrganizationUuid, sonarProjectId, mutationOptions, preselect } =
    options;
  const { data: workTypes, isLoading: loadingWorkTypes } = useJiraWorkTypesQuery(
    {
      sonarOrganizationUuid,
      sonarProjectId,
      jiraProjectKey: jiraProjectKey ?? '',
      includeFields: true,
    },
    { enabled: !!jiraProjectKey },
  );
  const { mutateAsync: patchJiraSelectedWorkTypes, isPending: savingWorkTypes } =
    usePatchJiraWorkTypesMutation();
  const [selectedWorkTypeIds, setSelectedWorkTypeIds] = useState<Array<string>>([]);

  const selectedRetrievedWorkTypeIds = useMemo(
    () => getSelectedWorkTypeIds(workTypes),
    [workTypes],
  );
  const hasSelectionChanged = useMemo(() => {
    return !isEqual(selectedWorkTypeIds, selectedRetrievedWorkTypeIds);
  }, [selectedWorkTypeIds, selectedRetrievedWorkTypeIds]);

  const someWorkTypeHasMandatoryFields = useMemo(
    () => workTypes?.some((workType) => getJiraWorkTypeMandatoryFields(workType).length > 0),
    [workTypes],
  );

  const saveWorkTypes = async () => {
    if (!hasSelectionChanged) {
      return;
    }

    await patchJiraSelectedWorkTypes(
      { sonarProjectId, selectedWorkTypes: selectedWorkTypeIds },
      mutationOptions,
      // catch can be handled through mutationOptions and error is handled on api level
    ).catch(noop);
  };

  const reset = useCallback(() => {
    setSelectedWorkTypeIds(selectedRetrievedWorkTypeIds);
  }, [selectedRetrievedWorkTypeIds]);

  useEffect(() => {
    if (!isDefined(workTypes)) {
      return;
    }

    const newSelection = preselect
      ? workTypes.filter((wt) => getJiraWorkTypeMandatoryFields(wt).length === 0).map((wt) => wt.id)
      : selectedRetrievedWorkTypeIds;

    setSelectedWorkTypeIds(newSelection);
  }, [workTypes, setSelectedWorkTypeIds, preselect, selectedRetrievedWorkTypeIds]);

  return {
    loadingWorkTypes,
    savingWorkTypes,
    workTypes,
    hasSelectionChanged,
    selectedWorkTypeIds,
    someWorkTypeHasMandatoryFields,
    saveWorkTypes,
    reset,
    setSelectedWorkTypeIds,
  };
}
